import * as React from "react";
import * as PropTypes from "prop-types";
import { merge, $, media } from "glamor";
import SendIcon from "material-ui/svg-icons/content/send";
import { Messages } from "./product-collection-chat";
import { theme } from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import Paper from "material-ui/Paper";
import IconButton from "material-ui/IconButton";
import { ConfirmDialog } from "components/dialog";
import { List, Map } from "immutable";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import { DATATEST } from "constants/integration";
import { ROOT_NAMESPACE } from "constants/product-collection";

interface IProps {
    app: any;
    onToggleSidePanel?: () => void;
    onGetCollectionChatMessages: (collectionId: string, options?: any) => void;
    onCreateCollectionChatMessage: (collectionId: string, data: any, meta?: any) => void;
    onDeleteCollectionChatMessage: (messageId: string, chatThreadId: string, collectionId: string) => void;
    onResetChatAlert: () => void;
    onResetChatState: ({ namespace, id, meta }) => void;
    chatMessageEntities: any;
    collection: any;
    collectionId: string;
    chatState: any;
    activeUserSessions: Map<string, any>;
}

interface IMessage {
    id: string;
    thread_id: string;
    namespace: string;
    identity: string;
    identity_type: string;
    kind: string;
    content: string;
}

export class Chat extends React.PureComponent<IProps, any> {
    static contextTypes = {
        me: PropTypes.object.isRequired,
    };

    styles = merge(
        {
            height: "100%",
        },
        $(" .backdrop", {
            display: "hidden",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
        }),
        $(" .chatContainer", {
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
            zIndex: 2,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            borderLeft: `1px solid ${theme.palette.borderColor}`,
        }),
        $(" .inputContainer", {
            height: "80px",
            width: "100%",
            backgroundColor: `${theme.palette.lightestGrey} !important`,
            paddingRight: "16px",
            alignItems: "center",
            zIndex: 10,
            flexShrink: 0,
            flexGrow: 0,
        }),
        $(" .inputContainer > textarea", {
            flexGrow: "1",
            border: "none",
            resize: "none",
            fontSize: "16px",
            padding: "26px 16px 0px 16px",
            width: "90%",
            backgroundColor: `${theme.palette.lightestGrey} !important`,
        }),

        $(" .alertWrapper", {
            position: "absolute",
            zIndex: 10,
            width: "100%",
        }),
        $(" .newMessagesAlert", {
            backgroundColor: theme.palette.blue,
            padding: "6px",
            borderRadius: "0px 0px 5px 5px",
            width: "50%",
            margin: "0 auto",
            textAlign: "center",
            color: theme.palette.canvasColor,
            cursor: "pointer",
            boxShadow: "rgba(0, 0, 0, 0.12) 0px 1px 6px",
        }),
        $(" .newMessagesAlert:hover", {
            backgroundColor: "#039BE5",
        }),
        $(" .closeChat", {
            display: "none !important",
            right: 0,
            top: 0,
            zIndex: 100,
            position: "absolute !important",
        }),
        media(
            "(max-width: 700px)",
            $(" .inputContainer > textarea", {
                width: "80%",
            })
        ),
        media(
            "(max-width: 500px)",
            {
                paddingLeft: "48px",
            },
            $(" .closeChat", {
                display: "block !important",
            }),
            $(" .backdrop", {
                display: "block",
            })
        )
    );

    list: any;

    muiStyles = {
        send: {
            active: {
                color: theme.palette.primary1Color,
                cursor: "pointer",
            },
            inactive: {
                color: theme.palette.disabledColor,
            },
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            message: "",
            selectedMessage: null,
            deleteDialogOpen: false,
            editing: false,
        };
    }

    UNSAFE_componentWillMount() {
        const { chatState, chatMessageEntities, collectionId } = this.props;
        const namespace = ROOT_NAMESPACE;
        const key = `${namespace}-${collectionId}`;
        if (chatMessageEntities && chatMessageEntities[key]) {
            this.props.onResetChatState({
                namespace,
                id: this.props.collectionId,
                meta: {
                    hasMore: chatState.hasMore,
                },
            });
        }
    }

    handleSendMessage = () => {
        const { me } = this.context;
        const data = {
            kind: "TEXT",
            content: this.state.message,
        };
        const meta = {
            identity: me._id,
            namespace: ROOT_NAMESPACE,
        };

        if (this.state.message) {
            this.props.onCreateCollectionChatMessage(this.props.collectionId, data, meta);
            this.setState({ message: "" });
            // this.list.scrollTop = this.list.scrollHeight;
        }
    };

    handleKeyPress = (event) => {
        if (!this.props.app.touchDevice) {
            if (event.key === "Enter" && event.shiftKey) {
                // like in slack, should open a newline
                event.preventDefault();
                this.setState({ message: this.state.message + "\n" });
            } else if (event.key === "Enter") {
                event.preventDefault();
                this.handleSendMessage();
            }
        }
    };

    handleChange = (event) => {
        this.setState({ message: event.target.value });
    };

    handleOpenDeleteDialog = (selectedMessage: IMessage) => {
        this.setState({ deleteDialogOpen: true, selectedMessage });
    };

    handleCloseDeleteDialog = () => {
        this.setState({ deleteDialogOpen: false, selectedMessage: null });
    };

    handleDeleteMessage = () => {
        const { selectedMessage } = this.state;
        const chatThreadId = `${ROOT_NAMESPACE}-${this.props.collectionId}`;
        this.props.onDeleteCollectionChatMessage(selectedMessage.id, chatThreadId, this.props.collectionId);
        this.handleCloseDeleteDialog();
    };

    handleSetListRef = (ref) => {
        this.list = ref;
    };

    handleInputFocus = () => {
        if (this.props.app.touchDevice) {
            this.setState({ editing: true });
        }
    };

    handleInputBlur = () => {
        if (this.props.app.touchDevice) {
            setTimeout(() => {
                this.setState({ editing: false });
            }, 100);
        }
    };

    handleToggleChat = () => {
        this.props.onToggleSidePanel();
    };

    render() {
        const {
            app,
            chatState,
            chatMessageEntities,
            collectionId,
            onGetCollectionChatMessages,
            onResetChatAlert,
            activeUserSessions,
        } = this.props;
        const { deleteDialogOpen } = this.state;
        const chatThreadId = `${ROOT_NAMESPACE}-${this.props.collectionId}`;

        let messages = List([]);
        if (chatMessageEntities && chatMessageEntities[chatThreadId]) {
            const chatEntity = chatMessageEntities[chatThreadId];
            messages = chatEntity.messages;
        }

        return (
            <div {...this.styles}>
                <div className="backdrop" onClick={this.handleToggleChat} />
                <div className="chatContainer">
                    <IconButton className="closeChat" onClick={this.handleToggleChat}>
                        <CloseIcon className="icon" />
                    </IconButton>
                    <Messages
                        app={app}
                        me={this.context.me}
                        collectionId={collectionId}
                        messages={messages}
                        chatState={chatState}
                        onOpenDeleteDialog={this.handleOpenDeleteDialog}
                        onGetCollectionChatMessages={onGetCollectionChatMessages}
                        onResetChatAlert={onResetChatAlert}
                        bindListReference={this.handleSetListRef}
                        activeUserSessions={activeUserSessions}
                        editing={this.state.editing}
                    />

                    <Paper className="inputContainer">
                        <textarea
                            data-test={DATATEST.ASSORTMENTS.CHAT_INPUT}
                            onFocus={this.handleInputFocus}
                            onBlur={this.handleInputBlur}
                            value={this.state.message}
                            onChange={this.handleChange}
                            onKeyPress={this.handleKeyPress}
                            placeholder={formatMessage("Send a message")}
                        />
                        <IconButton
                            data-test={DATATEST.ASSORTMENTS.CHAT_SEND}
                            onClick={this.handleSendMessage}
                            tooltip={this.state.message ? formatMessage("Send Message") : null}
                            style={{
                                position: "fixed",
                                right: "12px",
                                marginTop: "18px",
                            }}
                            tooltipPosition={"top-left"}
                            iconStyle={this.state.message ? this.muiStyles.send.active : this.muiStyles.send.inactive}
                        >
                            <SendIcon />
                        </IconButton>
                    </Paper>
                </div>

                <ConfirmDialog
                    title={formatMessage("Delete Message?")}
                    open={deleteDialogOpen}
                    onConfirm={this.handleDeleteMessage}
                    onCancel={this.handleCloseDeleteDialog}
                />
            </div>
        );
    }
}
